import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getOpenPositionsForMove, submitMoveRequest } from "../../../../../../api/macLeader";
import { SKILLS_REQUIRED_MESSAGE } from "./constants";

const FTE_OPTIONS = [0.25, 0.5, 0.75, 1.0];

const SkillToggle = ({ skill, selected, onToggle, t }) => (
  <Box
    onClick={() => onToggle(skill.skillId)}
    sx={{
      padding: "4px 10px", borderRadius: "3px", cursor: "pointer",
      border: selected
        ? `1px solid ${t.accent}88`
        : `1px solid ${t.border2}`,
      background: selected ? t.accent2 : t.surface2,
      fontFamily: "'DM Mono', monospace", fontSize: "10px",
      color: selected ? t.accent : t.text2,
      transition: "all 0.15s",
    }}
  >
    {selected ? "✓ " : ""}{skill.skillName}
  </Box>
);

const SectionLabel = ({ children, t }) => (
  <Typography sx={{
    fontFamily: "'DM Mono', monospace", fontSize: "8px",
    letterSpacing: "0.15em", textTransform: "uppercase",
    color: t.text3, mb: "8px", mt: "16px",
  }}>
    {children}
  </Typography>
);

const MoveWizard = ({ open, onClose, onSubmitted, sourceRequest }) => {
  const theme = useTheme();
  const t = theme.custom;
  const isDark = theme.palette.mode === "dark";

  const [positions, setPositions]         = useState([]);
  const [posLoading, setPosLoading]       = useState(false);
  const [selectedPos, setSelectedPos]     = useState(null);

  const [fte, setFte]                     = useState(null);
  const [sourceEndDate, setSourceEndDate] = useState("");
  const [startDate, setStartDate]         = useState("");
  const [endDate, setEndDate]             = useState("");
  const [notes, setNotes]                 = useState("");
  const [selectedSkillIds, setSelectedSkillIds] = useState([]);

  const [submitting, setSubmitting]       = useState(false);
  const [submitted, setSubmitted]         = useState(false);
  const [apiError, setApiError]           = useState(null);

  const [targetType, setTargetType] = useState("BIL");

  useEffect(() => {
    if (!open || !sourceRequest) return;

    // Reset state
    setSelectedPos(null);
    setTargetType("BIL");
    setFte(sourceRequest.fte ?? null);
    setSourceEndDate(sourceRequest.endDate ?? "");
    setStartDate("");
    setEndDate("");
    setNotes("");
    setSelectedSkillIds(
      (sourceRequest.deployedSkills ?? []).map((s) => s.skillId)
    );
    setApiError(null);
    setSubmitted(false);
  }, [open, sourceRequest]);

  // Reload positions whenever targetType changes
  useEffect(() => {
    if (!open || !sourceRequest) return;
    setSelectedPos(null);
    setPosLoading(true);
    getOpenPositionsForMove(targetType)
      .then(setPositions)
      .catch(() => setPositions([]))
      .finally(() => setPosLoading(false));
  }, [open, sourceRequest, targetType]);

  const toggleSkill = (skillId) => {
    setSelectedSkillIds((prev) =>
      prev.includes(skillId) ? prev.filter((s) => s !== skillId) : [...prev, skillId]
    );
  };

  const skills = sourceRequest?.associateSkills ?? [];
  const pickedSkillIds = selectedSkillIds.filter((id) => skills.some((sk) => sk.skillId === id));
  const skillsMissing = skills.length > 0 && pickedSkillIds.length === 0;

  const canSubmit =
    !!selectedPos && !!fte && !!sourceEndDate && !!startDate && !!endDate && !skillsMissing && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setApiError(null);
      await submitMoveRequest(sourceRequest.requestId, {
        targetPositionId: selectedPos.positionId,
        sourceEndDate,
        fte,
        startDate,
        endDate,
        notes: notes || null,
        deployedSkillIds: pickedSkillIds,
      });
      setSubmitted(true);
      onSubmitted?.();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.response?.data || "Something went wrong.";
      setApiError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <Box onClick={onClose} sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1200 }} />

      {/* Drawer */}
      <Box sx={{
        position: "fixed", top: 0, right: 0,
        width: "480px", height: "100vh",
        background: t.surface, borderLeft: `1px solid ${t.border2}`,
        zIndex: 1201, display: "flex", flexDirection: "column", overflow: "hidden",
      }}>

        {/* Header */}
        <Box sx={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 24px", borderBottom: `1px solid ${t.border2}`, flexShrink: 0,
        }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "15px", fontWeight: 700, color: t.text }}>
                Move Allocation
              </Typography>
              <Box sx={{ display: "flex", gap: "4px" }}>
                {["BIL", "UBL"].map((type) => (
                  <Box
                    key={type}
                    onClick={() => setTargetType(type)}
                    sx={{
                      borderRadius: "3px", padding: "2px 10px", cursor: "pointer",
                      background: targetType === type
                        ? (type === "UBL" ? "rgba(214,223,39,0.15)" : "rgba(40,179,180,0.15)")
                        : t.surface2,
                      border: `1px solid ${targetType === type
                        ? (type === "UBL" ? "rgba(214,223,39,0.45)" : "rgba(40,179,180,0.45)")
                        : t.border2}`,
                      transition: "all 0.15s",
                    }}
                  >
                    <Typography sx={{
                      fontFamily: "'DM Mono', monospace", fontSize: "8px",
                      color: targetType === type ? (type === "UBL" ? t.gold : t.accent) : t.text3,
                      letterSpacing: "0.1em", fontWeight: 600,
                    }}>
                      {sourceRequest?.positionType} → {type}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent, mt: "2px" }}>
              {sourceRequest?.requestCode}
            </Typography>
          </Box>
          <Box onClick={onClose} sx={{ cursor: "pointer", color: t.text3, fontSize: "18px", lineHeight: 1, padding: "4px", "&:hover": { color: t.text } }}>
            ✕
          </Box>
        </Box>

        {/* Locked associate + current position info */}
        <Box sx={{
          margin: "12px 24px 0", padding: "10px 14px",
          background: t.surface2, border: `1px solid ${t.border2}`,
          borderRadius: "4px", flexShrink: 0,
        }}>
          <Box sx={{ display: "flex", gap: "20px" }}>
            <Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "2px" }}>
                Associate
              </Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: t.text }}>
                {sourceRequest?.associateName}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: t.text3 }}>
                Level {sourceRequest?.associateLevelCode}
              </Typography>
            </Box>
            <Box sx={{ width: "1px", background: t.border2 }} />
            <Box>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "2px" }}>
                Current Position
              </Typography>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent }}>
                {sourceRequest?.positionCode}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: t.text2 }}>
                {sourceRequest?.roleTitle}
              </Typography>
            </Box>
          </Box>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mt: "8px", letterSpacing: "0.04em" }}>
            Associate is locked. Select a target {targetType} position below.
          </Typography>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "20px 24px", minHeight: 0 }}>

          {submitted ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pt: "60px", gap: "12px" }}>
              <Typography sx={{ fontSize: "24px" }}>✓</Typography>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "14px", fontWeight: 600, color: t.text }}>
                Move Request Submitted
              </Typography>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text3, textAlign: "center" }}>
                The request is now pending PMO approval.
              </Typography>
              <Box onClick={onClose} sx={{ mt: "16px", padding: "8px 24px", borderRadius: "3px", cursor: "pointer", background: t.accent, fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600, color: isDark ? "#060E0D" : "#fff" }}>
                Close
              </Box>
            </Box>
          ) : (
            <>
              {/* Target Position */}
              <SectionLabel t={t}>Select Target {targetType} Position *</SectionLabel>

              {posLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: "20px" }}>
                  <CircularProgress size={18} sx={{ color: t.accent }} />
                </Box>
              )}

              {!posLoading && positions.length === 0 && (
                <Box sx={{ padding: "10px 14px", background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: "3px" }}>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: t.text3 }}>
                    No available positions to move to. Contact PMO.
                  </Typography>
                </Box>
              )}

              {!posLoading && positions.map((p) => {
                const isSelected = selectedPos?.positionId === p.positionId;
                return (
                  <Box
                    key={p.positionId}
                    onClick={() => setSelectedPos(p)}
                    sx={{
                      border: isSelected ? `1px solid ${t.accent}88` : `1px solid ${t.border2}`,
                      background: isSelected ? t.accent2 : t.surface2,
                      borderRadius: "3px", padding: "10px 14px", mb: "6px",
                      cursor: "pointer", transition: "all 0.15s",
                      "&:hover": { borderColor: t.accent + "55" },
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Box>
  <Typography
    sx={{
      fontFamily: "'DM Mono', monospace",
      fontSize: "10px",
      color: t.accent,
      fontWeight: 500,
      mb: "2px",
    }}
  >
    {p.positionCode}
  </Typography>

  <Typography
    sx={{
      fontFamily: "'Inter', sans-serif",
      fontSize: "13px",
      fontWeight: 600,
      color: t.text,
      mb: "6px",
    }}
  >
    {p.roleTitle}
  </Typography>

  {/* SOW + Account pills */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      flexWrap: "wrap",
    }}
  >
    <Box
      sx={{
        background: t.surface3,
        borderRadius: "3px",
        padding: "2px 8px",
      }}
    >
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "8px",
          fontWeight: 600,
          color: t.text2,
          letterSpacing: "0.08em",
        }}
      >
        {p.sowName}
      </Typography>
    </Box>

    <Box
      sx={{
        background: t.surface3,
        borderRadius: "3px",
        padding: "2px 8px",
      }}
    >
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "8px",
          fontWeight: 600,
          color: t.text2,
          letterSpacing: "0.08em",
        }}
      >
        {p.accountName}
      </Typography>
    </Box>
  </Box>

  <Typography
    sx={{
      fontSize: "11px",
      color: t.text3,
      mt: "5px",
    }}
  >
    {p.macName} · Level {p.levelCode}
  </Typography>
</Box>
                      <Box sx={{
                        background: p.positionType === "BIL" ? t.accent2 : "rgba(214,223,39,0.1)",
                        border: `1px solid ${p.positionType === "BIL" ? t.accent + "44" : "rgba(214,223,39,0.3)"}`,
                        borderRadius: "2px", padding: "2px 8px", flexShrink: 0,
                      }}>
                        <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: p.positionType === "BIL" ? t.accent : t.gold, fontWeight: 600 }}>
                          {p.positionType === "BIL" ? "BILLED" : "UNBILLED"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}

              {/* Source End Date */}
              <SectionLabel t={t}>Current Allocation End Date *</SectionLabel>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, mb: "6px" }}>
                When does the current allocation end?
              </Typography>
              <input
                type="date"
                value={sourceEndDate}
                onChange={(e) => setSourceEndDate(e.target.value)}
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: "3px", boxSizing: "border-box",
                  border: `1px solid ${t.border2}`, background: t.surface2,
                  color: t.text, fontFamily: "'DM Mono', monospace", fontSize: "12px", outline: "none",
                  colorScheme: isDark ? "dark" : "light",
                }}
              />

              {/* FTE */}
              <SectionLabel t={t}>FTE *</SectionLabel>
              <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {FTE_OPTIONS.map((val) => (
                  <Box
                    key={val}
                    onClick={() => setFte(val)}
                    sx={{
                      padding: "6px 16px", borderRadius: "3px", cursor: "pointer",
                      border: fte === val ? `1px solid ${t.accent}88` : `1px solid ${t.border2}`,
                      background: fte === val ? t.accent2 : t.surface2,
                      fontFamily: "'DM Mono', monospace", fontSize: "12px", fontWeight: 600,
                      color: fte === val ? t.accent : t.text2, transition: "all 0.15s",
                    }}
                  >
                    {val}
                  </Box>
                ))}
              </Box>

              {/* New Start / End dates */}
              <SectionLabel t={t}>New Allocation Dates *</SectionLabel>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Box>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mb: "4px", letterSpacing: "0.1em" }}>
                    START DATE
                  </Typography>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{
                  width: "100%", padding: "8px 12px", borderRadius: "3px", boxSizing: "border-box",
                  border: `1px solid ${t.border2}`, background: t.surface2,
                  color: t.text, fontFamily: "'DM Mono', monospace", fontSize: "12px", outline: "none",
                  colorScheme: isDark ? "dark" : "light",
                }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: t.text3, mb: "4px", letterSpacing: "0.1em" }}>
                    END DATE
                  </Typography>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{
                  width: "100%", padding: "8px 12px", borderRadius: "3px", boxSizing: "border-box",
                  border: `1px solid ${t.border2}`, background: t.surface2,
                  color: t.text, fontFamily: "'DM Mono', monospace", fontSize: "12px", outline: "none",
                  colorScheme: isDark ? "dark" : "light",
                }}
                  />
                </Box>
              </Box>

              {/* Skills */}
              {skills.length > 0 && (
                <>
                  <SectionLabel t={t}>Skills to Deploy <span style={{ color: t.accent }}>*</span></SectionLabel>
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text3, mb: "8px", fontWeight: 300 }}>
                    Pre-filled from current allocation — toggle to adjust
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {skills.map((sk) => (
                      <SkillToggle
                        key={sk.skillId}
                        skill={sk}
                        selected={selectedSkillIds.includes(sk.skillId)}
                        onToggle={toggleSkill}
                        t={t}
                      />
                    ))}
                  </Box>
                  {skillsMissing && (
                    <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: "#c84040", mt: "6px" }}>
                      {SKILLS_REQUIRED_MESSAGE}
                    </Typography>
                  )}
                </>
              )}

              {/* Notes */}
              <SectionLabel t={t}>Notes</SectionLabel>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for move (optional)"
                rows={3}
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: "3px", boxSizing: "border-box",
                  border: `1px solid ${t.border2}`, background: t.surface2,
                  color: t.text, fontFamily: "'Inter', sans-serif", fontSize: "12px",
                  outline: "none", resize: "vertical",
                }}
              />

              {/* API Error */}
              {apiError && (
                <Box sx={{ mt: "12px", padding: "8px 12px", background: "rgba(200,64,64,0.08)", border: "1px solid rgba(200,64,64,0.3)", borderRadius: "3px" }}>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#c84040" }}>
                    {apiError}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Footer */}
        {!submitted && (
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 24px", borderTop: `1px solid ${t.border2}`, flexShrink: 0,
          }}>
            <Box onClick={onClose} sx={{
              padding: "8px 20px", borderRadius: "3px", cursor: "pointer",
              border: `1px solid ${t.border2}`, fontFamily: "'DM Mono', monospace",
              fontSize: "11px", color: t.text2, "&:hover": { borderColor: t.border3, color: t.text },
            }}>
              Cancel
            </Box>
            <Box
              onClick={canSubmit ? handleSubmit : undefined}
              sx={{
                padding: "8px 24px", borderRadius: "3px",
                cursor: canSubmit ? "pointer" : "not-allowed",
                background: canSubmit ? t.accent : t.surface3,
                fontFamily: "'DM Mono', monospace", fontSize: "11px", fontWeight: 600,
                color: canSubmit ? (isDark ? "#060E0D" : "#fff") : t.text3,
                display: "flex", alignItems: "center", gap: "8px",
                transition: "all 0.15s", "&:hover": canSubmit ? { opacity: 0.88 } : {},
              }}
            >
              {submitting && <CircularProgress size={10} sx={{ color: "inherit" }} />}
              {submitting ? "Submitting..." : "Submit Move Request"}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default MoveWizard;